import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useVideos } from "@/hooks/content";
import { useTranslatedList } from "@/hooks/useTranslatedList";

export default function VideosPage() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    document.title = "Video Center | L+TV TÉLÉVISION";
  }, []);

  const categories = [
    { value: "All", label: t("videos.cat.all") },
    { value: "News", label: t("videos.cat.news") },
    { value: "Interviews", label: t("videos.cat.interviews") },
    { value: "Reports", label: t("videos.cat.reports") },
    { value: "Talk Shows", label: t("videos.cat.talkshows") },
    { value: "Entertainment", label: t("videos.cat.entertainment") },
    { value: "Culture", label: t("videos.cat.culture") },
    { value: "Religion", label: t("videos.cat.religion") },
    { value: "Children's Programs", label: t("videos.cat.children") },
    { value: "Sports", label: t("videos.cat.sports") },
    { value: "Documentaries", label: t("videos.cat.documentaries") },
    { value: "Short Videos", label: t("videos.cat.short") },
    { value: "Behind the Scenes", label: t("videos.cat.behind") },
  ];

  const { items: allVideos } = useVideos();
  const filtered = allVideos.filter((v) => {
    const matchesCategory = activeCategory === "All" || v.category === activeCategory;
    const matchesSearch =
      searchQuery === "" ||
      v.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });
  const displayVideos = useTranslatedList(filtered, ["title", "description", "category"]);

  return (
    <div className="pt-20">
      <div className="bg-primary-900 py-8">
        <div className="ltv-container">
          <h1 className="text-2xl md:text-3xl font-bold text-white">{t("videos.title")}</h1>
          <p className="text-white/60 text-sm mt-1">{t("videos.subtitle")}</p>
        </div>
      </div>

      <div className="bg-white border-b border-background-200">
        <div className="ltv-container py-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                type="text"
                placeholder={t("videos.searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="ltv-input pl-10"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {categories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setActiveCategory(cat.value)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
                  activeCategory === cat.value
                    ? "bg-primary-500 text-white"
                    : "bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="ltv-container py-8 md:py-12">
        {displayVideos.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {displayVideos.map((video) => (
              <Link
                key={video.id}
                to={`/videos/${video.slug}`}
                className="ltv-card group"
              >
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <i className="ri-play-fill text-accent-500 text-xl ml-0.5"></i>
                    </div>
                  </div>
                  <div className="absolute bottom-2 right-2">
                    <span className="bg-black/70 text-white text-xs font-medium px-1.5 py-0.5 rounded">
                      {video.duration}
                    </span>
                  </div>
                  <div className="absolute top-2 left-2">
                    <span className="bg-primary-500 text-white text-xs font-semibold px-2 py-0.5 rounded">
                      {video.category}
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-foreground-900 leading-snug mb-2 line-clamp-2 group-hover:text-accent-600 transition-colors">
                    {video.title}
                  </h3>
                  <div className="flex items-center justify-between text-xs text-foreground-500">
                    <span>{video.publishedAt}</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {video.views}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <i className="ri-video-line text-4xl text-foreground-300 mb-3"></i>
            <p className="text-foreground-500">{t("videos.noResults")}</p>
          </div>
        )}
      </div>
    </div>
  );
}